import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import HistoryIcon from '@mui/icons-material/History';
import ChatIcon from '@mui/icons-material/Chat';
import { supplierAPI, billsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useNotify } from '../../context/NotifyContext';
import { useMessagingInbox } from '../../hooks/useMessagingInbox';
import AccountSettingsPanel from '../AccountSettingsPanel';
import { MessagingConversationListItem, MessagingMessageRow } from '../MessagingUI';
import { userDisplayLabel } from '../../utils/userDisplayLabel';
import { formatLocalDate, formatLocalDateTime } from '../../utils/formatLocalDate';
import { filterBySearch } from '../../utils/tableSearch';
import TableToolbar from '../TableToolbar';
import Pagination from '../Pagination';
import Modal from '../Modal';
import ConfirmModal from '../ConfirmModal';
import GradientSpinner from '../GradientSpinner';
import '../../App.css';

const SupplierDashboard = ({ activeTab: propTab }) => {
  const { user, refreshUser } = useAuth();
  const { showSuccess, showError } = useNotify();
  const supplierId = user?._id || user?.id;
  const [activeTab, setActiveTab] = useState(propTab || 'overview');
  const [availableRequests, setAvailableRequests] = useState([]);
  const [myOffers, setMyOffers] = useState([]);
  const [supplyHistory, setSupplyHistory] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [offerForm, setOfferForm] = useState({ requestId: '', totalPrice: '', items: '', message: '', deliveryTimeline: '' });
  const [requestForOfferModal, setRequestForOfferModal] = useState(null);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [showOfferDetails, setShowOfferDetails] = useState(false);
  const [withdrawOfferConfirm, setWithdrawOfferConfirm] = useState(null);
  const dashboard = { availableRequests, myOffers };

  const {
    conversations,
    messages: chatMessages,
    selectedConvId: selectedConv,
    setSelectedConvId: setSelectedConv,
    messageText,
    setMessageText,
    sendMessage,
    startConversation,
    loading: messagingLoading
  } = useMessagingInbox({
    enabled: activeTab === 'messaging',
    filterConversation: (c) => c.otherParty?.role === 'lab'
  });

  const labsForMessaging = (() => {
    const m = new Map();
    for (const r of [...availableRequests, ...myOffers]) {
      const lab = r.lab;
      if (lab?._id) m.set(String(lab._id), lab);
    }
    return [...m.values()];
  })();
  
  // Pagination states
  const [requestsPage, setRequestsPage] = useState(1);
  const [offersPage, setOffersPage] = useState(1);
  const [reqSearch, setReqSearch] = useState('');
  const [reqStatusFilter, setReqStatusFilter] = useState('');
  const [offersSearch, setOffersSearch] = useState('');
  const [supplierBillsSearch, setSupplierBillsSearch] = useState('');
  const [supplyHistorySearch, setSupplyHistorySearch] = useState('');
  const itemsPerPage = 10;

  useEffect(() => setRequestsPage(1), [reqSearch, reqStatusFilter]);
  useEffect(() => setOffersPage(1), [offersSearch]);

  useEffect(() => {
    if (propTab) setActiveTab(propTab);
  }, [propTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [reagentRes, myOffersRes] = await Promise.all([
        supplierAPI.getReagentRequests(),
        supplierAPI.getMyOffers()
      ]);
      if (reagentRes.data?.requests) setAvailableRequests(reagentRes.data.requests);
      if (myOffersRes.data?.requests) setMyOffers(myOffersRes.data.requests);
    } catch (err) {
      console.error('Supplier fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBills = async () => {
    try {
      const res = await billsAPI.getBills();
      if (res.data?.bills) setBills(res.data.bills);
    } catch (err) { console.error('Bills fetch error:', err); }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchBills();
  }, []);

  const handleSubmitOffer = async (e) => {
    e.preventDefault();
    try {
      const items =
        typeof offerForm.items === 'string'
          ? (() => {
              try {
                return JSON.parse(offerForm.items);
              } catch {
                return [{ reagentName: offerForm.items, price: 0, quantity: 1 }];
              }
            })()
          : offerForm.items;
      await supplierAPI.submitOffer(offerForm.requestId, {
        totalPrice: parseFloat(offerForm.totalPrice),
        items: Array.isArray(items) ? items : [items],
        message: offerForm.message
      });
      showSuccess('Offer submitted successfully!');
      setRequestForOfferModal(null);
      setOfferForm({ requestId: '', totalPrice: '', items: '', message: '', deliveryTimeline: '' });
      fetchData();
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to submit offer');
    }
  };

  const handleWithdrawOffer = (requestId, offerId) => {
    setWithdrawOfferConfirm({ requestId, offerId });
  };

  const handleViewOfferDetails = (request, offer) => {
    setSelectedOffer({ request, offer });
    setShowOfferDetails(true);
  };

  const filteredRequests = useMemo(() => {
    let rows = dashboard.availableRequests || [];
    if (reqStatusFilter) rows = rows.filter((r) => r.status === reqStatusFilter);
    return filterBySearch(rows, reqSearch, (r) => [
      r.title,
      r.description,
      r.status,
      r.lab?.labName,
      ...(r.reagents || []).map((x) => x.name)
    ]);
  }, [dashboard.availableRequests, reqSearch, reqStatusFilter]);

  const filteredOffersList = useMemo(
    () =>
      filterBySearch(dashboard.myOffers || [], offersSearch, (r) => [
        r.title,
        r.description,
        r.status,
        r.lab?.labName
      ]),
    [dashboard.myOffers, offersSearch]
  );

  const filteredSupplierBills = useMemo(
    () =>
      filterBySearch(bills || [], supplierBillsSearch, (b) => [
        b.invoiceNumber,
        b.status,
        b.amount,
        b.lab?.labName,
        b.type,
        b.reagentRequest?.title
      ]),
    [bills, supplierBillsSearch]
  );

  const filteredSupplyHistory = useMemo(
    () =>
      filterBySearch(supplyHistory || [], supplyHistorySearch, (row) => [
        row.requestTitle,
        row.labName,
        row.totalAmount,
        row.status,
        ...(row.items || []).map((i) => `${i.name} ${i.quantity} ${i.unit}`)
      ]),
    [supplyHistory, supplyHistorySearch]
  );

  const requestsStartIndex = (requestsPage - 1) * itemsPerPage;
  const requestsEndIndex = requestsStartIndex + itemsPerPage;
  const paginatedRequests = filteredRequests.slice(requestsStartIndex, requestsEndIndex);
  const totalRequestsPages = Math.max(1, Math.ceil(filteredRequests.length / itemsPerPage));

  const offersStartIndex = (offersPage - 1) * itemsPerPage;
  const offersEndIndex = offersStartIndex + itemsPerPage;
  const paginatedOffers = filteredOffersList.slice(offersStartIndex, offersEndIndex);
  const totalOffersPages = Math.max(1, Math.ceil(filteredOffersList.length / itemsPerPage));

  const getPageTitle = () => {
    switch (activeTab) {
      case 'requests': return 'Available Requests';
      case 'my-offers': return 'My Offers';
      case 'supply-history': return 'Supply History';
      case 'bills': return 'My Bills';
      case 'analytics': return 'Analytics';
      case 'messaging': return 'Messages';
      case 'settings': return 'Account';
      default: return 'Overview';
    }
  };

  return (
    <div>
      {activeTab !== 'overview' && (
        <h1 style={{ 
          fontSize: '1.5rem', 
          fontWeight: '600', 
          color: 'var(--text-primary)', 
          margin: '0 0 8px 0',
          paddingTop: '0'
        }}>
          {getPageTitle()}
        </h1>
      )}
      {activeTab === 'overview' && dashboard && (
        <>
        <div className="quick-actions">
          <Link to="/dashboard?tab=requests" className="quick-action-btn">
            <span className="quick-action-icon"><AssignmentIcon sx={{ fontSize: 28 }} /></span>
            <span>Available Requests</span>
          </Link>
          <Link to="/dashboard?tab=my-offers" className="quick-action-btn">
            <span className="quick-action-icon"><BusinessCenterIcon sx={{ fontSize: 28 }} /></span>
            <span>My Offers</span>
          </Link>
          <Link to="/dashboard?tab=supply-history" className="quick-action-btn">
            <span className="quick-action-icon"><HistoryIcon sx={{ fontSize: 28 }} /></span>
            <span>Supply History</span>
          </Link>
          <Link to="/dashboard?tab=messaging" className="quick-action-btn">
            <span className="quick-action-icon"><ChatIcon sx={{ fontSize: 28 }} /></span>
            <span>Messages</span>
          </Link>
        </div>
        <div className="card" style={{ marginBottom: '24px', padding: '16px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '1rem', color: 'var(--logo-cyan-teal)' }}>Subscription</h3>
          <p style={{ margin: 0, fontWeight: '600' }}>Current plan: Basic</p>
          <p style={{ margin: '4px 0 0 0', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Offers submitted this month: 8 of 30</p>
        </div>
        <div className="grid grid-2">
          <div className="card" style={{ 
            background: 'linear-gradient(135deg, rgba(51, 255, 217, 0.1), rgba(51, 255, 217, 0.05))',
            border: '1px solid rgba(51, 255, 217, 0.2)'
          }}>
            <h3 className="card-title" style={{ color: 'var(--logo-cyan-teal)' }}>Available Requests</h3>
            <p style={{ fontSize: '3rem', fontWeight: '700', color: 'var(--logo-cyan-teal)', margin: '16px 0 0 0' }}>
              {dashboard.availableRequests?.length || 0}
            </p>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '8px' }}>Open requests</p>
          </div>
          <div className="card" style={{ 
            background: 'linear-gradient(135deg, rgba(181, 255, 51, 0.1), rgba(181, 255, 51, 0.05))',
            border: '1px solid rgba(181, 255, 51, 0.2)'
          }}>
            <h3 className="card-title" style={{ color: 'var(--logo-lime-green)' }}>My Offers</h3>
            <p style={{ fontSize: '3rem', fontWeight: '700', color: 'var(--logo-lime-green)', margin: '16px 0 0 0' }}>
              {dashboard.myOffers?.length || 0}
            </p>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '8px' }}>Submitted offers</p>
          </div>
        </div>
        </>
      )}

      {activeTab === 'requests' && (
        <div>
          <Modal
            isOpen={!!requestForOfferModal}
            onClose={() => { setRequestForOfferModal(null); setOfferForm({ requestId: '', totalPrice: '', items: '', message: '', deliveryTimeline: '' }); }}
            title="Submit Offer"
          >
            {requestForOfferModal && (
              <>
                <div style={{
                  padding: '16px',
                  background: 'var(--bg-tertiary)',
                  borderRadius: '8px',
                  border: '1px solid var(--border-color)',
                  marginBottom: '20px'
                }}>
                  <h3 style={{ color: 'var(--logo-cyan-teal)', margin: '0 0 8px 0', fontSize: '1.125rem' }}>{requestForOfferModal.title}</h3>
                  <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '0.875rem' }}>{requestForOfferModal.description}</p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginTop: '12px' }}>
                    <div>
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Lab</p>
                      <p style={{ margin: 0, fontWeight: '500', fontSize: '0.875rem' }}>{requestForOfferModal.lab?.labName}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Budget</p>
                      <p style={{ margin: 0, fontWeight: '600', color: 'var(--logo-cyan-teal)', fontSize: '0.875rem' }}>${requestForOfferModal.budget}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Required By</p>
                      <p style={{ margin: 0, fontWeight: '500', fontSize: '0.875rem' }}>{formatLocalDate(requestForOfferModal.requiredBy)}</p>
                    </div>
                  </div>
                  {requestForOfferModal.reagents?.length > 0 && (
                    <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--border-color)' }}>
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '8px' }}>Reagents needed:</p>
                      {requestForOfferModal.reagents.map((r, idx) => (
                        <div key={idx} style={{ padding: '6px 0', fontSize: '0.875rem' }}>{r.name} — {r.quantity} {r.unit}</div>
                      ))}
                    </div>
                  )}
                </div>
                <form onSubmit={handleSubmitOffer}>
                  <div className="form-group">
                    <label className="form-label">Total Price ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-input"
                      value={offerForm.totalPrice}
                      onChange={(e) => setOfferForm({ ...offerForm, totalPrice: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Items (format: name:price:quantity, separated by commas)</label>
                    <textarea
                      className="form-textarea"
                      value={offerForm.items}
                      onChange={(e) => setOfferForm({ ...offerForm, items: e.target.value })}
                      placeholder="Reagent A:50:10, Reagent B:75:5"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Delivery timeline</label>
                    <input
                      type="text"
                      className="form-input"
                      value={offerForm.deliveryTimeline}
                      onChange={(e) => setOfferForm({ ...offerForm, deliveryTimeline: e.target.value })}
                      placeholder="e.g. 5-7 business days"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Message</label>
                    <textarea
                      className="form-textarea"
                      value={offerForm.message}
                      onChange={(e) => setOfferForm({ ...offerForm, message: e.target.value })}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                    <button type="submit" className="btn btn-primary">Submit Offer</button>
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={() => { setRequestForOfferModal(null); setOfferForm({ requestId: '', totalPrice: '', items: '', message: '', deliveryTimeline: '' }); }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </>
            )}
          </Modal>

          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 className="card-title" style={{ margin: 0 }}>Available Requests</h2>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                {dashboard.availableRequests?.length || 0}
              </div>
            </div>
            <TableToolbar
              search={reqSearch}
              onSearchChange={setReqSearch}
              placeholder="Search requests…"
              resultCount={filteredRequests.length}
              totalCount={dashboard.availableRequests?.length || 0}
            >
              <select
                className="form-select"
                value={reqStatusFilter}
                onChange={(e) => setReqStatusFilter(e.target.value)}
                aria-label="Filter by status"
              >
                <option value="">All statuses</option>
                <option value="open">open</option>
                <option value="bidding">bidding</option>
                <option value="ordered">ordered</option>
                <option value="delivered">delivered</option>
              </select>
            </TableToolbar>
            <div style={{ display: 'grid', gap: '16px' }}>
              {paginatedRequests.map((request) => (
                <div key={request._id} className="card" style={{ 
                  background: 'var(--bg-tertiary)',
                  border: '1px solid var(--border-color)',
                  padding: '20px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                    <div>
                      <h3 style={{ color: 'var(--logo-cyan-teal)', margin: '0 0 8px 0', fontSize: '1.25rem' }}>{request.title}</h3>
                      <p style={{ color: 'var(--text-secondary)', margin: 0 }}>{request.description}</p>
                    </div>
                    <span className="badge badge-warning">{request.status}</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginTop: '16px' }}>
                    <div>
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Lab</p>
                      <p style={{ margin: 0, fontWeight: '500' }}>{request.lab?.labName}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Budget</p>
                      <p style={{ margin: 0, fontWeight: '600', color: 'var(--logo-cyan-teal)' }}>${request.budget}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Required By</p>
                      <p style={{ margin: 0, fontWeight: '500' }}>{formatLocalDate(request.requiredBy)}</p>
                    </div>
                  </div>
                  <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border-color)' }}>
                    <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '8px' }}>Reagents Needed:</p>
                    <div style={{ display: 'grid', gap: '8px' }}>
                      {request.reagents?.map((reagent, idx) => (
                        <div key={idx} style={{ padding: '8px', background: 'var(--bg-secondary)', borderRadius: '6px' }}>
                          <p style={{ margin: 0, fontWeight: '500' }}>{reagent.name}</p>
                          <p style={{ margin: '4px 0 0 0', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                            {reagent.quantity} {reagent.unit} • {reagent.specifications}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={{ marginTop: '16px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <Link to="/dashboard?tab=messaging" className="btn btn-outline" style={{ padding: '8px 16px', minHeight: '44px', textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
                      Message
                    </Link>
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        setOfferForm({ ...offerForm, requestId: request._id });
                        setRequestForOfferModal(request);
                        setOfferForm(prev => ({ ...prev, requestId: request._id }));
                      }}
                    >
                      Submit Offer
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <Pagination
              currentPage={requestsPage}
              totalPages={totalRequestsPages}
              onPageChange={setRequestsPage}
              itemsPerPage={itemsPerPage}
              totalItems={filteredRequests.length}
            />
          </div>
        </div>
      )}

      {activeTab === 'my-offers' && (
        <div>
          <Modal
            isOpen={showOfferDetails && !!selectedOffer}
            onClose={() => { setShowOfferDetails(false); setSelectedOffer(null); }}
            title="Offer details"
            wide
          >
            {selectedOffer && (
              <div style={{ display: 'grid', gap: '16px' }}>
                <div>
                  <h3 style={{ color: 'var(--logo-cyan-teal)', margin: '0 0 8px 0' }}>{selectedOffer.request.title}</h3>
                  <p style={{ color: 'var(--text-secondary)', margin: '0 0 16px 0' }}>{selectedOffer.request.description}</p>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                  <div>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Offer amount</p>
                    <p style={{ margin: 0, fontWeight: '600', fontSize: '1.25rem', color: 'var(--logo-cyan-teal)' }}>
                      ${selectedOffer.offer.totalPrice}
                    </p>
                  </div>
                  <div>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Status</p>
                    <span className={`badge ${
                      selectedOffer.offer.status === 'accepted' ? 'badge-success' :
                      selectedOffer.offer.status === 'rejected' ? 'badge-danger' :
                      selectedOffer.offer.status === 'withdrawn' ? 'badge-info' : 'badge-warning'
                    }`}>
                      {selectedOffer.offer.status}
                    </span>
                  </div>
                  <div>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Request budget</p>
                    <p style={{ margin: 0, fontWeight: '500' }}>${selectedOffer.request.budget}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Required by</p>
                    <p style={{ margin: 0, fontWeight: '500' }}>{formatLocalDate(selectedOffer.request.requiredBy)}</p>
                  </div>
                </div>
                {selectedOffer.offer.message && (
                  <div>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 8px 0' }}>Message</p>
                    <p style={{ margin: 0, padding: '12px', background: 'var(--bg-tertiary)', borderRadius: '8px' }}>
                      {selectedOffer.offer.message}
                    </p>
                  </div>
                )}
                {selectedOffer.offer.items && selectedOffer.offer.items.length > 0 && (
                  <div>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 8px 0' }}>Items</p>
                    <div style={{ display: 'grid', gap: '8px' }}>
                      {selectedOffer.offer.items.map((item, idx) => (
                        <div key={idx} style={{ padding: '8px', background: 'var(--bg-tertiary)', borderRadius: '6px' }}>
                          <p style={{ margin: 0, fontWeight: '500' }}>{item.reagentName || item.name}</p>
                          <p style={{ margin: '4px 0 0 0', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                            ${item.price} × {item.quantity}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </Modal>
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
              <h2 className="card-title" style={{ margin: 0 }}>My Offers</h2>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                {dashboard.myOffers?.length || 0}
              </div>
            </div>
            <TableToolbar
              search={offersSearch}
              onSearchChange={setOffersSearch}
              placeholder="Search my offers / requests…"
              resultCount={filteredOffersList.length}
              totalCount={dashboard.myOffers?.length || 0}
            />
            <div style={{ display: 'grid', gap: '16px' }}>
              {paginatedOffers.map((request) => (
                <div key={request._id} className="card" style={{ 
                  background: 'var(--bg-tertiary)',
                  border: '1px solid var(--border-color)',
                  padding: '20px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                    <div>
                      <h3 style={{ color: 'var(--logo-cyan-teal)', margin: '0 0 8px 0', fontSize: '1.25rem' }}>{request.title}</h3>
                      <p style={{ color: 'var(--text-secondary)', margin: 0 }}>{request.description}</p>
                    </div>
                    <span className={`badge ${
                      request.status === 'delivered' ? 'badge-success' :
                      request.status === 'ordered' ? 'badge-info' : 'badge-warning'
                    }`}>
                      {request.status}
                    </span>
                  </div>
                  {request.offers?.filter(o => (o.supplier?._id || o.supplier)?.toString() === supplierId?.toString()).map((offer) => (
                    <div key={offer._id} style={{ 
                      marginTop: '16px', 
                      padding: '16px', 
                      background: 'var(--bg-secondary)', 
                      borderRadius: '8px',
                      border: '1px solid var(--logo-cyan-teal)'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '8px' }}>
                        <p style={{ margin: 0, fontWeight: '600', fontSize: '1.25rem', color: 'var(--logo-cyan-teal)' }}>
                          My Offer: ${offer.totalPrice}
                        </p>
                        <span className={`badge ${
                          offer.status === 'accepted' ? 'badge-success' :
                          offer.status === 'rejected' ? 'badge-danger' :
                          offer.status === 'withdrawn' ? 'badge-info' : 'badge-warning'
                        }`}>
                          {offer.status}
                        </span>
                      </div>
                      {offer.message && (
                        <p style={{ margin: '8px 0 12px 0', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                          {offer.message}
                        </p>
                      )}
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <Link to="/dashboard?tab=messaging" className="btn btn-outline" style={{ padding: '6px 16px', fontSize: '0.875rem', minHeight: '44px', textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
                          Message
                        </Link>
                        <button
                          className="btn btn-outline"
                          onClick={() => handleViewOfferDetails(request, offer)}
                          style={{ padding: '6px 16px', fontSize: '0.875rem', minHeight: '44px' }}
                        >
                          View Details
                        </button>
                        {offer.status === 'pending' && (
                          <button
                            className="btn btn-outline"
                            onClick={() => handleWithdrawOffer(request._id, offer._id)}
                            style={{ padding: '6px 16px', fontSize: '0.875rem', minHeight: '44px' }}
                          >
                            Withdraw Offer
                          </button>
                        )}
                        {offer.status === 'accepted' && request.status === 'ordered' && (
                          <button
                            className="btn btn-primary"
                            onClick={async () => {
                              try {
                                await billsAPI.createBillForSupply(request._id, { amount: offer.totalPrice });
                                showSuccess('Bill sent to lab successfully');
                                fetchBills();
                              } catch (err) {
                                showError(err.response?.data?.message || 'Failed to create bill');
                              }
                            }}
                            style={{ padding: '6px 16px', fontSize: '0.875rem', minHeight: '44px' }}
                          >
                            Send Bill to Lab
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
            <Pagination
              currentPage={offersPage}
              totalPages={totalOffersPages}
              onPageChange={setOffersPage}
              itemsPerPage={itemsPerPage}
              totalItems={filteredOffersList.length}
            />
          </div>
        </div>
      )}

      {activeTab === 'bills' && (
        <div className="card">
          <h2 className="card-title" style={{ marginBottom: '16px' }}>My Bills (Sent to Labs)</h2>
          <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '16px' }}>Bills you sent to labs for supply orders. Labs pay directly via your link, cash, or check.</p>
          <TableToolbar
            search={supplierBillsSearch}
            onSearchChange={setSupplierBillsSearch}
            placeholder="Search bills…"
            resultCount={filteredSupplierBills.length}
            totalCount={bills?.length || 0}
          />
          <div className="table-wrapper" style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr><th>Invoice</th><th>Lab</th><th>Amount</th><th>Order</th><th>Status</th></tr>
              </thead>
              <tbody>
                {filteredSupplierBills.map((b) => (
                  <tr key={b._id}>
                    <td style={{ fontWeight: '500' }}>{b.invoiceNumber}</td>
                    <td>{b.lab?.labName || b.lab?.name}</td>
                    <td style={{ fontWeight: '600' }}>${b.amount?.toLocaleString()}</td>
                    <td>{b.reagentRequest?.title || '—'}</td>
                    <td><span className={`badge ${b.status === 'paid' ? 'badge-success' : 'badge-warning'}`}>{b.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {(!filteredSupplierBills || filteredSupplierBills.length === 0) && <p style={{ color: 'var(--text-secondary)', padding: '24px', textAlign: 'center' }}>No bills match. When a lab accepts your offer, click &quot;Send Bill to Lab&quot; on the order.</p>}
        </div>
      )}

      {activeTab === 'supply-history' && (
        <div className="card">
          <h2 className="card-title" style={{ marginBottom: '16px' }}>Supply History</h2>
          <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '16px' }}>Orders delivered to Labs.</p>
          <TableToolbar
            search={supplyHistorySearch}
            onSearchChange={setSupplyHistorySearch}
            placeholder="Search history…"
            resultCount={filteredSupplyHistory.length}
            totalCount={supplyHistory?.length || 0}
          />
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th>Request</th>
                  <th>Lab</th>
                  <th>Amount</th>
                  <th>Items</th>
                  <th>Delivered</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredSupplyHistory.map((row) => (
                  <tr key={row._id}>
                    <td style={{ fontWeight: '500' }}>{row.requestTitle}</td>
                    <td>{row.labName}</td>
                    <td style={{ color: 'var(--logo-cyan-teal)', fontWeight: '600' }}>${row.totalAmount?.toLocaleString()}</td>
                    <td style={{ fontSize: '0.875rem' }}>{row.items?.map(i => `${i.name} (${i.quantity} ${i.unit})`).join(', ')}</td>
                    <td>{row.deliveredAt ? formatLocalDate(row.deliveredAt) : '-'}</td>
                    <td><span className="badge badge-success">{row.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'analytics' && (
        <div className="grid grid-3" style={{ gap: '24px' }}>
          <div className="card">
            <h3 className="card-title" style={{ marginBottom: '12px' }}>Offers Submitted by Month</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {(analytics?.offersByMonth || [])?.map((d, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{d.month}</span>
                  <span style={{ fontWeight: '600', color: 'var(--logo-cyan-teal)' }}>{d.count}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="card">
            <h3 className="card-title" style={{ marginBottom: '12px' }}>Revenue by Month</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {(analytics?.revenueByMonth || [])?.map((d, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{d.month}</span>
                  <span style={{ fontWeight: '600', color: 'var(--logo-lime-green)' }}>${d.amount?.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="card">
            <h3 className="card-title" style={{ marginBottom: '12px' }}>Summary</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Accepted offers</span>
                <span style={{ fontWeight: '600', color: 'var(--logo-cyan-teal)' }}>{analytics?.acceptedOffers ?? 0}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'settings' && (
        <AccountSettingsPanel
          user={user}
          refreshUser={refreshUser}
          onMessage={(m) => {
            if (!m) return;
            const s = String(m).trim();
            if (/^error\s*:/i.test(s)) showError(s.replace(/^error\s*:\s*/i, ''));
            else showSuccess(s);
          }}
        />
      )}

      {activeTab === 'messaging' && (
        <div className="card messaging-card">
          <h2 className="card-title">Messages (with Labs - reagent related)</h2>
          <div style={{ marginBottom: '12px', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
            <label className="form-label" style={{ margin: 0 }}>New message</label>
            <select
              className="form-input"
              style={{ maxWidth: '280px' }}
              defaultValue=""
              aria-label="Message a lab from reagent activity"
              onChange={(e) => {
                const v = e.target.value;
                if (v) startConversation(v);
                e.target.value = '';
              }}
            >
              <option value="">Select a lab…</option>
              {labsForMessaging.map((lab) => (
                <option key={lab._id} value={lab._id}>{lab.labName || lab.name || lab._id}</option>
              ))}
            </select>
            {messagingLoading && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                <GradientSpinner size={22} />
                Loading…
              </span>
            )}
          </div>
          <div className="messaging-layout">
            <div className="messaging-conversation-list">
              {conversations.map((conv) => (
                <MessagingConversationListItem
                  key={conv._id}
                  conv={conv}
                  selectedConvId={selectedConv}
                  onSelect={setSelectedConv}
                  title={userDisplayLabel(conv.otherParty)}
                />
              ))}
            </div>
            <div className="messaging-thread">
              {selectedConv ? (
                <>
                  <div className="messaging-messages">
                    {chatMessages.map((msg) => (
                      <MessagingMessageRow key={msg._id} msg={msg} meUser={user} formatLocalDateTime={formatLocalDateTime} />
                    ))}
                  </div>
                  <form onSubmit={sendMessage} className="messaging-composer">
                    <input type="file" accept="image/*,.pdf" multiple style={{ maxWidth: '120px', padding: '4px', fontSize: '0.75rem' }} title="Attach" disabled />
                    <input type="text" className="form-input" placeholder="Type a message..." value={messageText} onChange={(e) => setMessageText(e.target.value)} />
                    <button type="submit" className="btn btn-primary" disabled={!messageText.trim()}>Send</button>
                  </form>
                </>
              ) : (
                <div className="messaging-empty">Select a conversation</div>
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!withdrawOfferConfirm}
        onCancel={() => setWithdrawOfferConfirm(null)}
        onConfirm={() => {
          const { requestId, offerId } = withdrawOfferConfirm || {};
          setWithdrawOfferConfirm(null);
          if (!requestId || !offerId) return;
          setMyOffers((prev) =>
            prev.map((req) => {
              if (req._id === requestId) {
                return {
                  ...req,
                  offers: req.offers?.map((offer) =>
                    offer._id === offerId ? { ...offer, status: 'withdrawn' } : offer
                  )
                };
              }
              return req;
            })
          );
          showSuccess('Offer withdrawn.');
        }}
        title="Withdraw offer"
        message="Are you sure you want to withdraw this offer?"
        confirmLabel="Yes, withdraw"
        danger
      />
    </div>
  );
};

export default SupplierDashboard;
